import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ACTIONS, EventData, EVENTS, Joyride, STATUS, Step } from "react-joyride";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/zustand/Zustand";
import { TourContext, TourContextValue } from "./TourContext";
import { getTourForPath } from "./registry";
import { isTourSeen, markTourSeen, migrateLocalStorageTours, resetTourSeen } from "./storage";

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { t } = useTranslation("tours");
  const location = useLocation();
  const { userData } = useAuth();
  const userId = userData?.id;
  const seenTours = userData?.seenTours;

  const tourSpec = useMemo(() => getTourForPath(location.pathname), [location.pathname]);

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const activeTourIdRef = useRef<string | null>(null);
  const migratedUserIdRef = useRef<string | null>(null);

  const steps: Step[] = useMemo(() => {
    if (!tourSpec) return [];
    // Give each step a longer window for its target to mount; the default 1s
    // is too tight for pages that finish loading after navigation.
    return tourSpec.buildSteps(t).map((step) => ({ targetWaitTimeout: 3000, ...step }));
  }, [tourSpec, t]);

  useEffect(() => {
    if (!userId) return;
    if (migratedUserIdRef.current === userId) return;
    migratedUserIdRef.current = userId;
    void migrateLocalStorageTours(userId);
  }, [userId]);

  useEffect(() => {
    if (!tourSpec || !userId) return;
    if (isTourSeen(seenTours, tourSpec.id, tourSpec.version)) return;
    if (activeTourIdRef.current === tourSpec.id) return;

    const firstStep = steps[0];
    const firstTarget = typeof firstStep?.target === "string" ? firstStep.target : null;

    // Poll for the first step's target before starting the tour. Without this,
    // slow-loading pages can leave Joyride showing the dark overlay before the
    // target ever mounts, and its built-in 1s wait isn't always enough.
    let cancelled = false;
    let pollId: number | undefined;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 8000;
    const POLL_MS = 100;
    const INITIAL_DELAY_MS = 300;

    const start = () => {
      activeTourIdRef.current = tourSpec.id;
      setStepIndex(0);
      setRun(true);
    };

    const tryStart = () => {
      if (cancelled) return;
      if (!firstTarget || document.querySelector(firstTarget)) {
        start();
        return;
      }
      if (Date.now() - startedAt >= MAX_WAIT_MS) return;
      pollId = window.setTimeout(tryStart, POLL_MS);
    };

    pollId = window.setTimeout(tryStart, INITIAL_DELAY_MS);
    return () => {
      cancelled = true;
      if (pollId !== undefined) window.clearTimeout(pollId);
    };
  }, [tourSpec, userId, seenTours, steps]);

  useEffect(() => {
    if (activeTourIdRef.current && tourSpec?.id !== activeTourIdRef.current) {
      setRun(false);
      activeTourIdRef.current = null;
      setStepIndex(0);
    }
  }, [tourSpec]);

  const handleEvent = useCallback(
    (data: EventData) => {
      const { status, action, index, type } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false);
        if (tourSpec && userId) {
          void markTourSeen(tourSpec.id, tourSpec.version);
        }
        activeTourIdRef.current = null;
        setStepIndex(0);
        return;
      }

      if (type === EVENTS.TARGET_NOT_FOUND) {
        // Stop cleanly instead of advancing — otherwise the dark overlay can
        // get stuck when subsequent targets are also missing. Mark the tour as
        // seen too: a step whose target isn't in the DOM (e.g. a collapsed
        // sidebar hiding the element) would otherwise relaunch the tour on
        // every visit only to die at the same step again.
        setRun(false);
        if (tourSpec && userId) {
          void markTourSeen(tourSpec.id, tourSpec.version);
        }
        activeTourIdRef.current = null;
        setStepIndex(0);
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        if (action === ACTIONS.CLOSE) {
          setRun(false);
          if (tourSpec && userId) {
            void markTourSeen(tourSpec.id, tourSpec.version);
          }
          activeTourIdRef.current = null;
          setStepIndex(0);
          return;
        }
        setStepIndex(action === ACTIONS.PREV ? index - 1 : index + 1);
      }
    },
    [tourSpec, userId]
  );

  const startCurrentTour = useCallback(() => {
    if (!tourSpec) return;
    if (userId) void resetTourSeen(tourSpec.id);
    activeTourIdRef.current = tourSpec.id;
    setStepIndex(0);
    setRun(true);
  }, [tourSpec, userId]);

  const value: TourContextValue = useMemo(
    () => ({
      startCurrentTour,
      hasTourForCurrentRoute: !!tourSpec,
      isTourRunning: run,
    }),
    [startCurrentTour, tourSpec, run]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {steps.length > 0 && (
        <Joyride
          steps={steps}
          run={run}
          stepIndex={stepIndex}
          onEvent={handleEvent}
          continuous
          scrollToFirstStep
          // Constrain the popup to the browser viewport instead of the
          // nearest overflow ancestor; without this, steps that target tall
          // elements inside scroll containers (e.g. realm-stats table) can
          // render with the tooltip pushed offscreen.
          floatingOptions={{
            flipOptions: { rootBoundary: "viewport", padding: 16 },
            shiftOptions: { rootBoundary: "viewport", padding: 16 },
          }}
          options={{
            primaryColor: "#eab308",
            backgroundColor: "#2a2f3a",
            textColor: "#e5e7eb",
            arrowColor: "#2a2f3a",
            overlayColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 10000,
            showProgress: true,
          }}
          locale={{
            back: t("controls.back"),
            close: t("controls.close"),
            last: t("controls.last"),
            next: t("controls.next"),
            skip: t("controls.skip"),
          }}
          styles={{
            tooltip: {
              borderRadius: 8,
              fontSize: 14,
            },
            buttonPrimary: {
              backgroundColor: "#eab308",
              color: "#1e2026",
              borderRadius: 6,
              padding: "8px 14px",
              fontWeight: 600,
            },
            buttonBack: {
              color: "#9ca3af",
              marginRight: 8,
            },
            buttonSkip: {
              color: "#9ca3af",
            },
          }}
        />
      )}
    </TourContext.Provider>
  );
}
